import React, { useState, useEffect, useRef } from "react";
import { FaTerminal } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { baseURL } from "../../App";
import "./cli.css";

const CLIModal = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [inputVal, setInputVal] = useState("");
  const [history, setHistory] = useState([
    {
      type: "welcome",
      content:
        "Welcome to Aubin SIAHA's Developer Terminal (v2.0)!\nType 'help' to see available commands or 'exit' to close.",
    },
  ]);
  const [cmdHistoryIndex, setCmdHistoryIndex] = useState(-1);
  const [cmdHistory, setCmdHistory] = useState([]);

  // Dynamic backend data state
  const [backendData, setBackendData] = useState({
    about: null,
    experiences: [],
    projects: [],
    contact: null,
  });

  const inputRef = useRef(null);
  const bodyRef = useRef(null);

  // Fetch live portfolio data from backend CMS endpoints
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [aboutRes, expRes, portRes, contactRes] = await Promise.allSettled([
          axios.get(`${baseURL}/api/about`),
          axios.get(`${baseURL}/api/experience`),
          axios.get(`${baseURL}/api/portfolio`),
          axios.get(`${baseURL}/api/contact`),
        ]);

        setBackendData({
          about:
            aboutRes.status === "fulfilled" && aboutRes.value.data.length
              ? aboutRes.value.data[aboutRes.value.data.length - 1]
              : null,
          experiences:
            expRes.status === "fulfilled" && Array.isArray(expRes.value.data)
              ? expRes.value.data
              : [],
          projects:
            portRes.status === "fulfilled" && Array.isArray(portRes.value.data)
              ? portRes.value.data
              : [],
          contact:
            contactRes.status === "fulfilled" && contactRes.value.data.length
              ? contactRes.value.data[contactRes.value.data.length - 1]
              : null,
        });
      } catch (err) {
        console.error("CLIModal backend fetch error:", err);
      }
    };

    fetchAllData();
  }, []);

  // Keyboard shortcut listener (Ctrl + K or `)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey && e.key.toLowerCase() === "k") || e.key === "`") {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      } else if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Auto-scroll to bottom of CLI output
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [history, isOpen]);

  const handleCommand = (e) => {
    e.preventDefault();
    const command = inputVal.trim();
    if (!command) return;

    setCmdHistory((prev) => [...prev, command]);
    setCmdHistoryIndex(-1);

    const newHistory = [...history, { type: "command", content: command }];
    const cmdLower = command.toLowerCase();
    const isFr = i18n.language === "fr";

    switch (cmdLower) {
      case "help":
        newHistory.push({
          type: "output",
          content:
            "Available Commands:\n" +
            "  help      - Show this help menu\n" +
            "  about     - Biography, degree & software engineering background\n" +
            "  skills    - List tech stack (Frontend, Backend, Databases, Cloud)\n" +
            "  projects  - Overview of recent portfolio projects\n" +
            "  contact   - Get email, LinkedIn, and WhatsApp info\n" +
            "  theme     - Toggle light / dark theme\n" +
            "  github    - Open Aubin's GitHub profile\n" +
            "  clear     - Clear terminal screen\n" +
            "  exit      - Close terminal modal",
        });
        break;

      case "about":
        const aboutDesc = backendData.about?.description
          ? isFr
            ? backendData.about.description.fr
            : backendData.about.description.en
          : isFr
          ? "Aubin SIAHA | Ingénieur Informaticien en Génie Logiciel (FET, Université de Buea).\nSpécialiste du développement web full stack, sécurité informatique, GCP et solutions M365 Business Premium."
          : "Aubin SIAHA | Software Engineer & Full Stack Developer (FET, University of Buea).\nSpecializing in scalable web applications, IT security, GCP cloud, and M365 solutions.";

        const identity = backendData.about?.identity || "Aubin SIAHA";
        newHistory.push({
          type: "output-accent",
          content: `${identity}\n${aboutDesc}`,
        });
        break;

      case "skills":
        let skillItems = [];

        if (backendData.experiences.length > 0) {
          skillItems = backendData.experiences.map((exp) => {
            const catName = isFr ? exp.categoryFr : exp.categoryEn;
            const skillList = exp.skills
              .map((s) => (isFr ? s.nameFr : s.nameEn))
              .join(", ");
            return { label: `${catName}:`, value: skillList };
          });
        } else {
          skillItems = [
            {
              label: "Frontend:",
              value:
                "Next.js, React.js, JavaScript (ES6+), HTML5, CSS3, i18next, Bootstrap, TailwindCSS",
            },
            {
              label: "Backend:",
              value:
                "Nest.js, Node.js, Express.js, TypeScript, REST APIs, JWT Auth, Java",
            },
            {
              label: "Databases:",
              value: "MongoDB (Mongoose), PostgreSQL, MySQL, Cloudinary",
            },
            {
              label: "Cloud & Ops:",
              value:
                "Google Cloud Platform (GCP), Microsoft 365 Business Premium, Linux, Git, Docker",
            },
          ];
        }

        newHistory.push({
          type: "output-table",
          items: skillItems,
        });
        break;

      case "projects":
        let projectText = "Featured Portfolio Projects:\n";
        if (backendData.projects.length > 0) {
          backendData.projects.forEach((proj, idx) => {
            const title = isFr ? proj.titleFr : proj.titleEn;
            projectText += ` ${idx + 1}. ${title}\n`;
          });
          projectText += "Type 'github' to view source code repositories!";
        } else {
          projectText +=
            " 1. MAAUTECH Learn (Platforme d'apprentissage en ligne)\n" +
            " 2. Garage Automobile Website\n" +
            " 3. Menu Numérique pour Restaurant (ReactJS)\n" +
            " 4. Full-Stack Dynamic Portfolio & CMS\n" +
            "Type 'github' to view source code repositories!";
        }

        newHistory.push({
          type: "output-success",
          content: projectText,
        });
        break;

      case "contact":
        const email = backendData.contact?.email || "aubinsiaha9@gmail.com";
        const linkedin =
          backendData.contact?.linkedin || "https://www.linkedin.com/in/aubinsiaha";
        const whatsapp = backendData.contact?.whatsapp || "+237...";

        newHistory.push({
          type: "output",
          content:
            "Contact Channels:\n" +
            `  Email:      ${email}\n` +
            `  LinkedIn:   ${linkedin}\n` +
            `  WhatsApp:   ${whatsapp}\n` +
            "  GitHub:     https://github.com/Aubin9\n" +
            "  YouTube:    https://www.youtube.com/@maau_tech",
        });
        break;

      case "theme":
        document.body.classList.toggle("dark-mode");
        const isDark = document.body.classList.contains("dark-mode");
        localStorage.setItem("theme", isDark ? "dark" : "light");
        newHistory.push({
          type: "output-success",
          content: `Switched theme to ${isDark ? "Dark Mode 🌙" : "Light Mode ☀️"}`,
        });
        break;

      case "github":
        window.open("https://github.com/Aubin9", "_blank");
        newHistory.push({
          type: "output-success",
          content: "Opening GitHub profile in a new tab...",
        });
        break;

      case "clear":
        setHistory([]);
        setInputVal("");
        return;

      case "exit":
      case "close":
        setIsOpen(false);
        setInputVal("");
        return;

      default:
        newHistory.push({
          type: "output",
          content: `Command not recognized: '${command}'. Type 'help' for a list of valid commands.`,
        });
        break;
    }

    setHistory(newHistory);
    setInputVal("");
  };

  const handleKeyDownInput = (e) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (cmdHistory.length === 0) return;
      const nextIndex =
        cmdHistoryIndex === -1
          ? cmdHistory.length - 1
          : Math.max(0, cmdHistoryIndex - 1);
      setCmdHistoryIndex(nextIndex);
      setInputVal(cmdHistory[nextIndex] || "");
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (cmdHistoryIndex === -1) return;
      const nextIndex = cmdHistoryIndex + 1;
      if (nextIndex >= cmdHistory.length) {
        setCmdHistoryIndex(-1);
        setInputVal("");
      } else {
        setCmdHistoryIndex(nextIndex);
        setInputVal(cmdHistory[nextIndex] || "");
      }
    }
  };

  return (
    <>
      <button
        className="cli-trigger-btn"
        onClick={() => setIsOpen(true)}
        title="Open Developer Terminal (Ctrl + K)"
      >
        <FaTerminal />
      </button>

      {isOpen && (
        <div className="cli-modal-overlay" onClick={() => setIsOpen(false)}>
          <div
            className="cli-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cli-header">
              <div className="cli-title">
                <FaTerminal style={{ color: "#00ff99" }} />
                <span>aubin@portfolio:~ (zsh)</span>
              </div>
              <div className="cli-dots">
                <div
                  className="cli-dot close"
                  onClick={() => setIsOpen(false)}
                  title="Close"
                ></div>
                <div className="cli-dot minimize"></div>
                <div className="cli-dot maximize"></div>
              </div>
            </div>

            <div className="cli-body" ref={bodyRef}>
              {history.map((item, idx) => (
                <div key={idx} className="cli-line">
                  {item.type === "welcome" && (
                    <div className="cli-welcome">{item.content}</div>
                  )}

                  {item.type === "command" && (
                    <div className="cli-command-line">
                      <span className="cli-prompt">aubin@dev:~$ </span>
                      <span>{item.content}</span>
                    </div>
                  )}

                  {item.type === "output" && (
                    <div className="cli-output">{item.content}</div>
                  )}

                  {item.type === "output-success" && (
                    <div className="cli-output cli-output-success">
                      {item.content}
                    </div>
                  )}

                  {item.type === "output-accent" && (
                    <div className="cli-output cli-output-accent">
                      {item.content}
                    </div>
                  )}

                  {item.type === "output-table" && (
                    <div className="cli-output">
                      {item.items.map((row, rIdx) => (
                        <div key={rIdx} className="cli-output-table">
                          <span style={{ color: "#58a6ff" }}>{row.label}</span>
                          <span>{row.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <form onSubmit={handleCommand} className="cli-input-form">
                <span className="cli-prompt">aubin@dev:~$ </span>
                <input
                  ref={inputRef}
                  type="text"
                  className="cli-input"
                  value={inputVal}
                  onChange={(e) => setInputVal(e.target.value)}
                  onKeyDown={handleKeyDownInput}
                  placeholder="Type a command (e.g. 'help')..."
                  autoFocus
                />
              </form>
              <div className="cli-hint">Tip: Use Ctrl+K or ` to toggle terminal</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CLIModal;
